'use client';

import Icon from '../atoms/Icon';
import MembershipAlert from '../molecules/MembershipAlert';
import MembershipHeader from './MembershipHeader';
import HouseholdMemberList from './HouseholdMemberList';
import type { MembershipMember, MembershipStatus, TabName } from '../types';
import { getStoredSession } from '@/lib/auth/client';
import type { MembershipMemberCard } from '@/lib/api/types';
import { extractYear, formatDate } from '@/lib/utils';
import { useMembership } from '../context/MembershipContext';
import { useProfileData } from '../context/ProfileContext';

/** Convert a MembershipMemberCard to the MembershipMember shape used by MembershipCard. */
function toMembershipMember(card: MembershipMemberCard): MembershipMember {
  return {
    name: card.name,
    id: card.lookupId,
    memberSince: card.joinDate ? extractYear(card.joinDate) : undefined,
    expires: card.expirationDate ? formatDate(card.expirationDate) : undefined,
    isPrimary: card.isPrimary,
  };
}

interface SidebarProps {
  activeTab: TabName;
}

/**
 * @component Sidebar
 * @description Left-hand sidebar displaying welcome message, membership alerts,
 * a plan selector dropdown, and a scrollable list of member cards.
 * Supports both desktop sidebar and mobile stacked layout without duplicating logic.
 * Rendered inside an `<aside>` landmark for assistive technology.
 * @param activeTab - Currently active tab, used to conditionally show content on mobile.
 * @returns The sidebar `<aside>` element with all membership-related content.
 */
export default function Sidebar({ activeTab }: SidebarProps) {
  const { myProfile } = useProfileData();
  const session = getStoredSession();

  // Use live profile data as the source of truth; fall back to the stored
  // session so the name renders immediately before the API responds.
  const firstName = (myProfile?.firstName || session?.profile.firstName)?.split(' ')[0] ?? null;
  const fullName = myProfile
    ? [myProfile.firstName, myProfile.lastName].filter(Boolean).join(' ')
    : [session?.profile.firstName, session?.profile.lastName].filter(Boolean).join(' ');
  const {
    memberships, memberCards, selectedMembershipId, setSelectedMembershipId, isLoaded,
    hasMembership, isActiveMember, isRecentlyExpiredMember, expirationDate,
  } = useMembership();

  // Derive selected membership and its member cards
  const selectedMembership =
    memberships.find((m) => m.membershipId === selectedMembershipId) ?? memberships[0] ?? null;
  const selectedCards = selectedMembershipId
    ? memberCards.filter((c) => c.membershipId === selectedMembershipId)
    : memberCards;
  const primaryCard = selectedCards.find((c) => c.isPrimary);
  const otherCards = selectedCards.filter((c) => !c.isPrimary);
  const membershipStatus: MembershipStatus = isActiveMember ? 'ACTIVE' : 'EXPIRED';
  const membershipType = primaryCard?.membershipLevel ?? '';
  const hasCards = selectedCards.length > 0;

  // Fallback primary member from session when cards haven't loaded yet
  const fallbackPrimaryMember: MembershipMember = {
    name: fullName || 'Member',
    id: selectedMembership?.membershipId ?? '',
    memberSince: undefined,
    expires: selectedMembership?.expirationDate
      ? formatDate(selectedMembership.expirationDate)
      : undefined,
    isPrimary: true,
  };

  const primaryMember = primaryCard ? toMembershipMember(primaryCard) : fallbackPrimaryMember;
  const allMembers: MembershipMember[] = [
    primaryMember,
    ...otherCards.map(toMembershipMember),
  ];

  const showMobileMembership = activeTab === 'Dashboard';

  return (
    <aside
      aria-label="Membership sidebar"
      className="z-2 w-full lg:w-80 lg:shrink-0 bg-canvas lg:h-screen lg:sticky lg:top-0 overflow-y-auto lg:border-r border-border-light flex flex-col p-4 lg:p-6"
    >
      <div className="w-full flex flex-col justify-start items-start gap-5">
        {/* Welcome Icon - hidden on mobile (shown in mobile header) */}
        <div className="hidden lg:flex flex-col justify-center items-start gap-4">
          <Icon
            name="welcome"
            className="w-24 h-16"
            alt="Grounds for Sculpture logo"
            decorative={false}
          />
        </div>

        {/* Welcome message — always visible */}
        <div className="self-stretch pb-px flex flex-col justify-start items-start">
          <h1 className="text-heading text-charcoal">
            Welcome{firstName ? `, ${firstName}` : ""}!
          </h1>
        </div>

        {/*
          Membership States:
          - active                        -> MembershipHeader + HouseholdMemberList
          - active + renewal window       -> Renew alert inside MembershipHeader + HouseholdMemberList
          - expired within 2 months       -> Expired alert inside MembershipHeader + HouseholdMemberList
          - expired >2 months / no member -> Start membership CTA

          On mobile, membership content appears on Dashboard only; on other tabs, show welcome only
        */}
        <div
          className={`w-full flex flex-col gap-4 ${showMobileMembership ? "" : "hidden lg:flex"}`}
        >
          {/* Loading skeleton — matches MembershipCard shape */}
          {!isLoaded && (
            <div className="w-full flex flex-col gap-4 animate-pulse">
              {/* Header skeleton */}
              <div className="h-10 bg-muted-bg" />
              {/* Primary card skeleton */}
              <div className="bg-white border border-border-light p-4 flex flex-col gap-6">
                <div className="flex flex-col gap-1.5">
                  <div className="flex items-center justify-between">
                    <div className="h-3 w-24 bg-muted-bg" />
                    <div className="h-5 w-14 bg-muted-bg" />
                  </div>
                  <div className="h-5 w-36 bg-muted-bg" />
                </div>
                <div className="flex items-end justify-between">
                  <div className="flex flex-col gap-1">
                    <div className="h-3 w-28 bg-muted-bg" />
                    <div className="h-3 w-24 bg-muted-bg" />
                    <div className="h-3 w-20 bg-muted-bg mt-1" />
                  </div>
                  <div className="w-12 h-12 bg-muted-bg" />
                </div>
              </div>
            </div>
          )}

          {/* State: None - no membership, CTA to start */}
          {isLoaded && !hasMembership && (
            <MembershipAlert variant="start" />
          )}

          {/* State: Expired >2 months ago — treat as no membership */}
          {isLoaded && hasMembership && !isActiveMember && !isRecentlyExpiredMember && (
            <MembershipAlert variant="start" />
          )}

          {/* State: Active or recently expired — header (with alert) + member cards */}
          {isLoaded && hasCards && (isActiveMember || isRecentlyExpiredMember) && (
            <>
              <MembershipHeader
                expirationDate={expirationDate}
                isInRenewalWindow={selectedMembership?.isInRenewalWindow}
                membershipStatus={membershipStatus}
                memberships={memberships}
                selectedMembershipId={selectedMembershipId}
                onMembershipChange={setSelectedMembershipId}
                fallbackOption={
                  !selectedMembership && primaryCard
                    ? { id: primaryCard.membershipLookupId, label: primaryCard.membershipLevel ? `${primaryCard.membershipLevel} Membership` : 'Membership' }
                    : undefined
                }
              />
              <HouseholdMemberList
                members={allMembers}
                membershipType={membershipType}
                membershipStatus={membershipStatus}
              />
            </>
          )}
        </div>

      </div>
    </aside>
  );
}
